<template>
  <div>
    <ViewLoadingMessage :loading="firstLoading"/>

    <CRow v-if="!firstLoading">
      <CCol lg="12">
        <CCard class="no-borders">
          <CCardHeader class="mb-4">
            <div class="d-flex justify-content-between align-items-center">
              <div class="d-flex align-items-center">
                <h1 class="main-header mr-3 mb-0">
                  <translate translate-context="edge-prints.list">Orders</translate>
                </h1>

                <template v-if="filtersApplied">
                  <span class="mr-3">
                    <translate translate-context="edge-prints.list">Found %{ totalOrders } results</translate>
                  </span>

                  <CButton @click="clearFilters" color="secondary" size="sm">
                    <translate translate-context="edge-prints.list">Reset</translate>
                  </CButton>
                </template>
              </div>

            </div>
          </CCardHeader>

          <CCardBody>
            <CDataTable
              :items="orders"
              :fields="fields"
              :hover="false"
              :striped="true"
              :bordered="true"
              :small="false"
              :fixed="false"
              :loading="loading.orders"
              :items-per-page="20"
              :column-filter="{ external: true, lazy: true }"
              :column-filter-value="localFilters"
              @update:column-filter-value="filterResults($event)"
              :no-items-view="{
                noResults: $pgettext('edge-prints.list', 'No filtering results available'),
                noItems: $pgettext('edge-prints.list', 'No orders found')
              }"
              class="custom-table"
            >
              <!-- Transaction number -->
              <template #transaction_number="{item}">
                <td>
                  <router-link
                    v-if="hasAnyPerms('edge.print.detail')"
                    :to="{
                      name: 'edgePrintOrderDetail',
                      params: {
                        picaServiceId: picaServiceId,
                        orderId: item.id
                      }
                    }"
                    class="link--order font-weight-bold"
                  >
                    {{ getTransactionNumber(item) }}
                  </router-link>
                  <span v-else class="font-weight-bold">
                    {{ getTransactionNumber(item) }}
                  </span>
                </td>
              </template>

              <!-- Transaction number filter -->
              <template #transaction_number-filter="{ item }">
                <CInput
                  size="sm"
                  v-model="localFilters.transaction_number"
                  :placeholder="$gettext('Search...')"
                  @keyup.enter="onFilterChange"
                />
              </template>

              <!-- Created at -->
              <template #created_at="{item}">
                <td>{{ item.created_at | formatDate('DD/MM/YYYY HH:mm') }}</td>
              </template>

              <!-- Created at filter -->
              <template #created_at-filter="{ item }">
                <div>
                  <VueCtkDateTimePicker
                    v-model="localFilters.created_at_range"
                    :auto-close="false"
                    @validate="onFilterChange()"
                    range
                    :no-button="false"
                    :no-shortcuts="false"
                    :input-size="`sm`"
                    :position="`bottom`"
                    :no-label="true"
                    :only-date="true"
                    label=""
                    format="YYYY-MM-DD"
                    formatted="DD/MM/YYYY"
                    :right="false"
                    :no-header="true"
                    :no-button-now="false"
                    :placeholder="$gettext('Select date range')"
                  />
                </div>
              </template>

              <!-- Cabin -->
              <template #cabin="{item}">
                <td>{{ getCabinId(item) }}</td>
              </template>

              <!-- Cabin filter -->
              <template #cabin-filter="{ item }">
                <CInput
                  size="sm"
                  v-model="localFilters.cabin_id"
                  :placeholder="$gettext('Search...')"
                  @keyup.enter="onFilterChange"
                />
              </template>

              <!-- Guest -->
              <template #guest="{item}">
                <td>{{ getGuestId(item) }}</td>
              </template>

              <!-- Guest filter -->
              <template #guest-filter="{ item }">
                <CInput
                  size="sm"
                  v-model="localFilters.guest_id"
                  :placeholder="$gettext('Search...')"
                  @keyup.enter="onFilterChange"
                />
              </template>

              <!-- PicaCode -->
              <template #picacode="{item}">
                <td>
                  <template v-if="item.picacodes && item.picacodes.length > 0">
                    <CBadge
                      v-for="(code, index) in item.picacodes"
                      :key="index"
                      color="light"
                      class="badge-outline mr-1"
                    >
                      {{ code }}
                    </CBadge>
                  </template>
                  <span v-else>-</span>
                </td>
              </template>

              <!-- PicaCode filter -->
              <template #picacode-filter="{ item }">
                <CInput
                  size="sm"
                  v-model="localFilters.picacode"
                  :placeholder="$gettext('Search...')"
                  @keyup.enter="onFilterChange"
                />
              </template>

              <!-- Status -->
              <template #status="{item}">
                <td class="text-nowrap">
                  <CBadge class="badge-outline" shape="pill" color="light">
                    <div class="d-inline-flex align-items-center py-1">
                      <CIcon name="cipDot" :class="getStatusColorClass(item.status)" size="custom-size" :height="6"/>
                      <span class="ml-1">{{ getStatusLabel(item.status) }}</span>
                    </div>
                  </CBadge>
                </td>
              </template>

              <!-- Status filter -->
              <template #status-filter="{ item }">
                <CSelect
                  size="sm"
                  class="mb-0"
                  :placeholder="$gettext('select')"
                  :options="printStatusOptions"
                  v-model="localFilters.print_status"
                  @change="onFilterChange"
                />
              </template>

              <!-- Payment Platform -->
              <template #payment_platform="{item}">
                <td>
                  {{ item.payment_platform ? getPaymentPlatformLabel(item.payment_platform) : '-' }}
                </td>
              </template>

              <!-- Payment Platform filter -->
              <template #payment_platform-filter="{ item }">
                <CSelect
                  size="sm"
                  class="mb-0"
                  :placeholder="$gettext('select')"
                  :options="paymentPlatformOptions"
                  v-model="localFilters.payment_platform"
                  @change="onFilterChange"
                />
              </template>

              <!-- Total amount -->
              <template #total_amount="{item}">
                <td>
                  {{ item.total_amount | currency(item.currency) }}
                </td>
              </template>

              <!-- Actions -->
              <template #actions="{item}">
                <td class="text-nowrap sticky-column">
                  <div class="d-flex align-items-center">
                    <!-- View Details Button -->
                    <CButton
                      color="dark"
                      variant="outline"
                      class="btn-circle btn-circle--sm d-inline-flex align-items-center justify-content-center"
                      size="sm"
                      :to="{
                        name: 'edgePrintOrderDetail',
                        params: {
                          picaServiceId: picaServiceId,
                          orderId: item.id
                        }
                      }"
                    >
                      <CIcon name="cipArrowRight"/>
                    </CButton>
                  </div>
                </td>
              </template>

              <!-- No items -->
              <template #no-items-view>
                <div class="text-center py-4">
                  <CIcon name="cipPackage" size="3xl" class="text-secondary mb-3"/>
                  <h6 class="text-muted">
                    <translate translate-context="edge-prints.list">No orders found</translate>
                  </h6>
                </div>
              </template>
            </CDataTable>

            <!-- Pagination -->
            <CPagination
              v-if="totalPages > 1"
              :activePage.sync="currentPage"
              :pages="totalPages"
              @update:activePage="onPageChange"
              align="center"
              class="mt-3"
            />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ViewLoadingMessage from '@/components/ViewLoadingMessage.vue'
import { edgeOrdersFields } from '../constants/index'
import OrderStatus, {
  getOrderStatusOptions,
  getOrderStatusLabel,
  getOrderStatusColor
} from '@/domain/core/constant/orderStatus'
import { getPaymentPlatformLabel, getPaymentPlatformFilterOptions } from '@/domain/core/constant/paymentPlatform'
import Filters from '@/domain/core/utils/filters'

export default {
  name: 'EdgeOrderList',
  components: {
    ViewLoadingMessage
  },
  data() {
    return {
      firstLoading: true,
      localFilters: {
        print_status: 'to_print', // Default: show only orders to print (PROCESSING)
        payment_platform: '', // Default: empty string (All)
        order_number: '',
        transaction_number: '',
        cabin_id: '',
        guest_id: '',
        picacode: '',
        created_at_range: null
      },
      currentPage: 1
    }
  },
  computed: {
    ...mapGetters('edgePrints', [
      'orders',
      'loading',
      'filters',
      'totalOrders',
      'totalPages',
      'picaServiceId'
    ]),
    ...mapGetters('event', [
      'isInEvent'
    ]),
    fields() {
      // Filter fields based on permissions
      return Object.values(edgeOrdersFields).filter(field => {
        if (field.requiresPermission) {
          return this.hasAnyPerms(field.requiresPermission)
        }
        return true
      })
    },
    printStatusOptions() {
      return [
        { value: 'to_print', label: this.$pgettext('edge-prints.filter', 'To print') },
        { value: 'completed_only', label: this.$pgettext('edge-prints.filter', 'Completed only') },
        { value: 'all', label: this.$pgettext('edge-prints.filter', 'All') }
      ]
    },
    paymentPlatformOptions() {
      return getPaymentPlatformFilterOptions()
    },
    filtersApplied() {
      return this.localFilters.print_status !== 'to_print' ||
        this.localFilters.payment_platform !== '' ||
        this.localFilters.order_number !== '' ||
        this.localFilters.transaction_number !== '' ||
        this.localFilters.cabin_id !== '' ||
        this.localFilters.guest_id !== '' ||
        this.localFilters.picacode !== '' ||
        this.localFilters.created_at_range !== null
    }
  },
  methods: {
    ...mapActions('edgePrints', [
      'fetchOrders',
      'updateFilters',
      'setPicaServiceId'
    ]),
    ...mapActions('login', [
      'fetchConfig'
    ]),
    async loadOrders() {
      await this.fetchOrders()
    },
    filterResults(filters) {
      this.localFilters = { ...this.localFilters, ...filters }
      this.onFilterChange()
    },
    onFilterChange() {
      const filters = {
        sal_no: this.localFilters.order_number, // Backend uses sal_no for order number filtering
        transaction_number: this.localFilters.transaction_number,
        cabin_id: this.localFilters.cabin_id,
        guest_id: this.localFilters.guest_id,
        picacode: this.localFilters.picacode,
        created_at_range: this.localFilters.created_at_range,
        page: 1
      }

      // Convert print_status to status filter
      // to_print = PROCESSING (5), completed_only = COMPLETED (6), all = both
      if (this.localFilters.print_status === 'to_print') {
        filters.status = OrderStatus.PROCESSING.value
      } else if (this.localFilters.print_status === 'completed_only') {
        filters.status = OrderStatus.COMPLETED.value
      } else if (this.localFilters.print_status === 'all') {
        filters.status = `${OrderStatus.PROCESSING.value},${OrderStatus.COMPLETED.value}`
      }

      // Add payment platform filter (skip if empty)
      if (this.localFilters.payment_platform) {
        filters.payment_platform = this.localFilters.payment_platform
      }

      this.currentPage = 1
      this.updateFilters(filters)
      this.loadOrders()
    },
    clearFilters() {
      this.localFilters = {
        print_status: 'to_print',
        payment_platform: '',
        order_number: '',
        transaction_number: '',
        cabin_id: '',
        guest_id: '',
        picacode: '',
        created_at_range: null
      }
      this.onFilterChange()
    },
    onPageChange(page) {
      this.updateFilters({ page })
      this.loadOrders()
    },
    getStatusLabel(status) {
      return getOrderStatusLabel(status)
    },
    getStatusColorClass(status) {
      const color = getOrderStatusColor(status)

      // Map colors to text classes
      const colorMap = {
        'primary': 'text-primary',
        'success': 'text-success',
        'warning': 'text-warning',
        'danger': 'text-danger',
        'secondary': 'text-secondary',
        'dark': 'text-dark'
      }

      return colorMap[color] || 'text-secondary'
    },
    getSalNo(item) {
      return item.order_number || '-'
    },
    getCabinId(item) {
      return item.cabin_id || '-'
    },
    getGuestId(item) {
      return item.guest_id || '-'
    },
    getTransactionNumber(item) {
      return item.transaction_number || '-'
    },
    getPaymentPlatformLabel(platform) {
      return getPaymentPlatformLabel(platform)
    }
  },
  async mounted() {
    // Set picaServiceId from route
    const picaServiceId = this.$route.params.picaServiceId
    await this.setPicaServiceId(picaServiceId)

    await this.fetchConfig({
      type: 'edge_print',
      id: picaServiceId
    })

    // Load orders with default filters (show to print)
    await this.loadOrders()
    this.firstLoading = false
  },
  filters: {
    currency: Filters.currency,
    formatDate: Filters.formatDate
  }
}
</script>

<style scoped>
.link--order {
  font-weight: 600;
  color: #321fdb;
}

.link--order:hover {
  color: #2819a0;
  text-decoration: underline;
}

.pica-code {
  font-weight: 500;
}

.pica-code--sm {
  font-size: 0.875rem;
}

.custom-table {
  font-size: 0.875rem;
}

.sticky-column {
  position: sticky;
  right: 0;
  background: white;
  z-index: 1;
}
</style>